import { Button, Container, Grid, Paper, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { url } from "../../baseUrl";
import DatatablePage from "../../Components/DataTable/DataTable";
import CustomizedSnackbars from "../../Components/SnackBar/SnackBar";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { useSelector } from "react-redux";
import Navbar from "../../Components/Navbar/Navbar";
import RefreshIcon from "@mui/icons-material/Refresh";

// ─── E‑ComSnaps Brand Colors ───────────────────────────────
const colors = {
  primary: "#0006df",
  primaryDark: "#0a0fca",
  background: "#f5f3ff",
  white: "#ffffff",
  textPrimary: "#1a1a2e",
  success: "#16a34a",
  danger: "#dc2626",
};

// ─── USA TIME HELPER ──────────────────────────────────────────
const convertToUSATime = (dateTime) => {
  if (!dateTime) return '—';
  try {
    const date = new Date(dateTime);
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch (e) {
    return dateTime;
  }
};

const TransactionList = () => {
  const user = useSelector((state) => state.user.data);
  const [data, setData] = useState();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("");
  const navigate = useNavigate();

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  const columns = [
    { label: "Action", field: "action", width: 100 },
    { label: "Year", field: "year", width: 100 },
    { label: "Month", field: "month", width: 100 },
    { label: "Date", field: "dateTime", width: 200 },
    { label: "Customer Name", field: "custName", width: 250 },
    { label: "Email Address", field: "custEmail", width: 250 },
    { label: "Phone Number", field: "custContactNumber", width: 250 },
    { label: "Brand", field: "brand", width: 150 },
    { label: "Order ID", field: "orderId", width: 150 },
    { label: "Type Of Sale", field: "saleType", width: 150 },
    { label: "Sale's Person", field: "salePerson", width: 250 },
    { label: "Service", field: "detailDiscription", width: 150 },
    { label: "Total Amount", field: "total", width: 100 },
    { label: "Account", field: "account", width: 100 },
    { label: "Payment Status", field: "paymentStatus", width: 100 },
    { label: "Payment Time", field: "paymentTime", width: 100 },
    { label: "Description", field: "discription", width: 150 },
    { label: "Invoice‑Link", field: "invoiceLink", width: 300 },
    { label: "Stripe ID", field: "stripeId", width: 150 },
  ];

  useEffect(() => {
    getAllTransactions();
  }, []);

  const getAllTransactions = async () => {
    try {
      setData([]);
      setIsLoaded(true);

      const response = await axios.post(
        `${url}/transactions/getAllTransactions`,
        { brand: "ecomsnaps" },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const rows = response.data.data.map((item) => ({
        ...item,
        total: formatter.format(item.total),
        brand: "ecom",
        // ─── DATE COLUMN ────────────────────────────────────────────
        dateTime: convertToUSATime(item.dateTime),
        paymentStatus: item.paymentStatus ? (
          <Typography
            variant="button"
            sx={{
              padding: "6px 14px",
              background: colors.success,
              borderRadius: "20px",
              color: colors.white,
              fontWeight: 600,
              fontSize: "12px",
              display: "inline-block",
            }}
          >
            Paid
          </Typography>
        ) : (
          <Typography
            variant="button"
            sx={{
              padding: "6px 14px",
              background: colors.danger,
              borderRadius: "20px",
              color: colors.white,
              fontWeight: 600,
              fontSize: "12px",
              display: "inline-block",
            }}
          >
            Unpaid
          </Typography>
        ),
        // ─── PAYMENT TIME ──────────────────────────────────────────
        paymentTime: item.paymentStatus
          ? convertToUSATime(item.updatedAt)
          : "",
        action: (
          <span
            onClick={() =>
              navigate(`/transaction/record/${item._id}_readOnly`)
            }
            style={{
              color: colors.primary,
              cursor: "pointer",
            }}
            title="View Invoice"
          >
            <RemoveRedEyeIcon />
          </span>
        ),
      }));

      setData({ columns, rows });
      setIsOpen(true);
      setMessage("Data loaded successfully!");
      setSeverity("success");
    } catch (error) {
      console.error(error);
      setIsOpen(true);
      setMessage(error.message || "Failed to load data.");
      setSeverity("error");
    } finally {
      setIsLoaded(false);
    }
    setTimeout(() => setIsOpen(false), 2000);
  };

  return (
    <>
      <Navbar />
      <Container
        style={{
          maxWidth: "100%",
          padding: "0px",
          overflowY: "scroll",
          height: "94vh",
          background: colors.background,
        }}
      >
        <Paper
          fullWidth
          sx={{
            m: 1,
            p: 2,
            maxWidth: "100%",
            backgroundColor: colors.white,
            borderRadius: "16px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          }}
        >
          <Grid style={{ display: "flex", justifyContent: "flex-start" }}>
            <Typography
              variant="h5"
              style={{ color: colors.textPrimary, fontWeight: 700 }}
            >
              All Transactions
            </Typography>
          </Grid>

          <Grid
            xs={12}
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 16,
            }}
          >
            <Button
              onClick={getAllTransactions}
              variant="contained"
              sx={{
                background: colors.primary,
                color: colors.white,
                borderRadius: "12px",
                padding: "10px 20px",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "0 4px 12px rgba(0,6,223,0.3)",
                "&:hover": { background: colors.primaryDark },
              }}
              startIcon={<RefreshIcon />}
            >
              Reload
            </Button>

            <Button
              onClick={() => navigate(`/transaction/record/00000000000_CREATE`)}
              variant="contained"
              sx={{
                background: colors.primary,
                color: colors.white,
                borderRadius: "12px",
                padding: "10px 20px",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "0 4px 12px rgba(0,6,223,0.3)",
                "&:hover": { background: colors.primaryDark },
              }}
            >
              Generate New Link
            </Button>
          </Grid>

          <DatatablePage data={data} isLoaded={isLoaded} />
        </Paper>
      </Container>

      <CustomizedSnackbars isOpen={isOpen} severity={severity} message={message} />
    </>
  );
};

export default TransactionList;